import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Users, Search, Pencil, KeyRound, CheckCircle2, Ban, Mail, ShieldCheck, ShieldOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_BADGE = {
  ADMIN: "bg-rose-100 text-rose-800 border-rose-300",
  MERCHANT: "bg-blue-100 text-blue-900 border-blue-300",
  CUSTOMER: "bg-stone-100 text-stone-700 border-stone-300",
};

const fmtDate = (s) => s ? new Date(s).toLocaleDateString("pt-BR") : "—";
const fmtBRL = (v) => `B$ ${(Number(v) || 0).toFixed(2).replace(".", ",")}`;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (roleFilter !== "all") params.role = roleFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (search) params.search = search;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao carregar"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [roleFilter, statusFilter]);
  // Re-busca ao digitar (com debounce de 400ms)
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const stats = useMemo(() => {
    return {
      total: users.length,
      customers: users.filter((u) => u.role === "CUSTOMER").length,
      merchants: users.filter((u) => u.role === "MERCHANT").length,
      admins: users.filter((u) => u.role === "ADMIN").length,
      unverified: users.filter((u) => !u.email_verified && u.role !== "ADMIN").length,
      inactive: users.filter((u) => u.active === false).length,
    };
  }, [users]);

  const save = async () => {
    setBusy(true);
    try {
      const { id, name, email, phone, role, active, email_verified } = editing;
      await api.patch(`/admin/users/${id}`, { name, email, phone, role, active, email_verified });
      toast.success("Usuário atualizado");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
    finally { setBusy(false); }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Gerar nova senha temporária para ${u.email}?`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      window.prompt("Senha temporária (envie ao usuário com segurança):", data.temp_password);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
  };

  const forceVerify = async (u) => {
    if (!window.confirm(`Marcar ${u.email} como verificado manualmente?`)) return;
    try { await api.post(`/admin/users/${u.id}/force-verify`); toast.success("E-mail verificado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
  };

  const toggleActive = async (u) => {
    const goingInactive = u.active !== false;
    if (goingInactive && !window.confirm(`Desativar ${u.email}? Ele não conseguirá logar.`)) return;
    try {
      if (goingInactive) await api.delete(`/admin/users/${u.id}`);
      else await api.patch(`/admin/users/${u.id}`, { active: true });
      toast.success(goingInactive ? "Usuário desativado" : "Usuário reativado");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 flex items-center gap-2"><Users size={26}/> Gestão de Usuários</h1>
          <p className="text-stone-500 text-sm mt-0.5">Visualize, edite, redefina senha, desative e verifique contas.</p>
        </div>
        <div className="flex gap-2 flex-wrap text-xs">
          <span className="px-3 py-1.5 rounded-lg bg-stone-100 text-stone-700 font-bold">Total: {stats.total}</span>
          <span className="px-3 py-1.5 rounded-lg bg-stone-50 text-stone-700">Clientes: <strong>{stats.customers}</strong></span>
          <span className="px-3 py-1.5 rounded-lg bg-blue-50 text-blue-900">Comerciantes: <strong>{stats.merchants}</strong></span>
          <span className="px-3 py-1.5 rounded-lg bg-rose-50 text-rose-900">Admins: <strong>{stats.admins}</strong></span>
          <span className="px-3 py-1.5 rounded-lg bg-amber-50 text-amber-900">Não verificados: <strong>{stats.unverified}</strong></span>
          <span className="px-3 py-1.5 rounded-lg bg-red-50 text-red-700">Inativos: <strong>{stats.inactive}</strong></span>
        </div>
      </div>

      <Card className="p-3 rounded-2xl border-stone-200 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
          <div className="sm:col-span-2 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Buscar nome, e-mail, telefone ou wallet code..." className="rounded-xl pl-9 h-10" data-testid="users-search"/>
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="rounded-xl h-10" data-testid="users-role-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas roles</SelectItem>
              <SelectItem value="CUSTOMER">Cliente</SelectItem>
              <SelectItem value="MERCHANT">Comerciante</SelectItem>
              <SelectItem value="ADMIN">Admin</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-xl h-10" data-testid="users-status-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos status</SelectItem>
              <SelectItem value="active">Ativos</SelectItem>
              <SelectItem value="inactive">Inativos</SelectItem>
              <SelectItem value="unverified">E-mail não verificado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="rounded-2xl border-stone-200 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-stone-400"><Loader2 className="animate-spin mx-auto" size={28}/></div>
        ) : users.length === 0 ? (
          <p className="text-center text-stone-500 text-sm p-10" data-testid="users-empty">Nenhum usuário encontrado.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-600">
                <tr>
                  <th className="text-left px-3 py-2">Usuário</th>
                  <th className="text-left px-3 py-2">Role</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Telefone</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Saldo</th>
                  <th className="text-left px-3 py-2 hidden lg:table-cell">Criado</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className={`border-t border-stone-100 ${u.active === false ? "opacity-60" : ""}`} data-testid={`user-row-${u.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-stone-900">{u.name}</div>
                      <div className="text-[11px] text-stone-500 truncate max-w-[220px]">{u.email}</div>
                      {u.wallet_code && <div className="text-[10px] text-stone-400 font-mono mt-0.5">{u.wallet_code}</div>}
                    </td>
                    <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${ROLE_BADGE[u.role] || "bg-stone-100"}`}>{u.role}</span></td>
                    <td className="px-3 py-2 hidden md:table-cell text-stone-600 text-xs">{u.phone || "—"}</td>
                    <td className="px-3 py-2 hidden md:table-cell font-mono text-xs text-emerald-700">{u.role === "CUSTOMER" ? fmtBRL(u.wallet_balance) : "—"}</td>
                    <td className="px-3 py-2 hidden lg:table-cell text-stone-500 text-xs">{fmtDate(u.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-0.5 text-[10px]">
                        {u.active === false ? (
                          <span className="inline-flex items-center gap-1 text-red-700 font-bold uppercase"><Ban size={10}/>Inativo</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-bold uppercase"><CheckCircle2 size={10}/>Ativo</span>
                        )}
                        {u.role !== "ADMIN" && !u.email_verified && (
                          <span className="inline-flex items-center gap-1 text-amber-700 font-bold uppercase"><Mail size={10}/>Não verif.</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setEditing({ ...u })} title="Editar" className="h-8 w-8 text-stone-500 hover:text-blue-900" data-testid={`user-edit-${u.id}`}>
                          <Pencil size={14}/>
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => resetPassword(u)} title="Redefinir senha" className="h-8 w-8 text-stone-500 hover:text-amber-700" data-testid={`user-reset-${u.id}`}>
                          <KeyRound size={14}/>
                        </Button>
                        {u.role !== "ADMIN" && !u.email_verified && (
                          <Button size="icon" variant="ghost" onClick={() => forceVerify(u)} title="Forçar verificação" className="h-8 w-8 text-stone-500 hover:text-emerald-700" data-testid={`user-verify-${u.id}`}>
                            <ShieldCheck size={14}/>
                          </Button>
                        )}
                        {u.role !== "ADMIN" && (
                          <Button size="icon" variant="ghost" onClick={() => toggleActive(u)} title={u.active === false ? "Reativar" : "Desativar"} className={`h-8 w-8 ${u.active === false ? "text-emerald-600" : "text-red-500"} hover:opacity-80`} data-testid={`user-toggle-${u.id}`}>
                            {u.active === false ? <ShieldCheck size={14}/> : <ShieldOff size={14}/>}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <Dialog open onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent className="max-w-md rounded-2xl">
            <DialogHeader><DialogTitle className="font-display text-xl">Editar usuário</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Nome</Label><Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-xl mt-1" data-testid="user-edit-name"/></div>
              <div><Label className="text-xs">E-mail</Label><Input type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="rounded-xl mt-1" data-testid="user-edit-email"/></div>
              <div><Label className="text-xs">Telefone</Label><Input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} placeholder="(99) 99999-9999" className="rounded-xl mt-1" data-testid="user-edit-phone"/></div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="user-edit-role"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CUSTOMER">Cliente</SelectItem>
                    <SelectItem value="MERCHANT">Comerciante</SelectItem>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Switch checked={editing.active !== false} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="user-edit-active"/>
                <Label className="text-sm">Ativo</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!editing.email_verified} onCheckedChange={(v) => setEditing({ ...editing, email_verified: v })} data-testid="user-edit-verified"/>
                <Label className="text-sm">E-mail verificado</Label>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditing(null)} className="rounded-xl flex-1">Cancelar</Button>
              <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl flex-1" data-testid="user-edit-save">Salvar</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
